import type { Duration } from 'tinyduration'

/**
 * Helper function to delay execution
 * @param {number} ms - milliseconds to wait
 * @returns Promise that resolves after the specified delay
 */
export function delay(ms: number) {
	return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Helper function to convert time to milliseconds
 * @param amount - The amount of time
 * @param unit - The unit of time
 * @returns Milliseconds, or `0` when `unit` is omitted/unrecognized
 */
export function time(amount: number, unit?: 's' | 'm' | 'h' | 'd') {
	if (unit === 's') return amount * 1000
	if (unit === 'm') return amount * 60 * 1000
	if (unit === 'h') return amount * 60 * 60 * 1000
	if (unit === 'd') return amount * 24 * 60 * 60 * 1000

	// Footgun: silently returns 0 when no unit matches. e.g. `time(30)` → 0ms,
	// which disables staleTime/gcTime (treated as "always stale") instead of
	// the intended 30s. Always pass a unit.
	return 0
}

/**
 * Format a tinyduration Duration object as a user-friendly string.
 * Returns the first non-zero unit (e.g., '2 months'), or '0 seconds' if all are zero/undefined.
 * @param durationObj - The duration object to format
 */
export function formatDuration(durationObj?: Duration): string {
	if (!durationObj) return ''
	const units: Array<[keyof Duration, string]> = [
		['years', 'year'],
		['months', 'month'],
		['weeks', 'week'],
		['days', 'day'],
		['hours', 'hour'],
		['minutes', 'minute'],
		['seconds', 'second'],
	]
	for (const [key, label] of units) {
		const value = durationObj[key]
		if (typeof value === 'number' && value > 0) {
			return `${value} ${label}${value === 1 ? '' : 's'}`
		}
	}
	return '0 seconds'
}
